import { PayloadAction } from '@reduxjs/toolkit';

/**
 * Standard filter state shape used across multiple slices
 */
export interface FilterState {
    filters: {
        keywords: string[];
    };
}

/**
 * Dropdown filter configuration
 */
export interface DropdownFilter {
    keys: string[];
    asKeyword: boolean;
}

/**
 * Extended filter state with status dropdown support
 */
export interface StatusDropdownFilterState {
    filters: {
        keywords: string[];
        dropdowns: {
            status: DropdownFilter;
        };
    };
}

/**
 * Extended filter state with dropdown support
 */
export interface DropdownFilterState {
    filters: {
        keywords: string[];
        dropdowns: {
            claimDataTypeName: DropdownFilter;
        };
    };
}

/**
 * Creates the initial state for a filter-based slice
 */
export const createFilterInitialState = (): FilterState => ({
    filters: {
        keywords: [],
    },
});

/**
 * Creates the initial state for a filter-based slice with dropdown support
 */
export const createDropdownFilterInitialState = (): DropdownFilterState => ({
    filters: {
        keywords: [],
        dropdowns: {
            claimDataTypeName: {
                keys: [],
                asKeyword: false,
            },
        },
    },
});

/**
 * Base filter reducers for addNewFilter and removeFilter.
 * These are reused across all filter reducer factories.
 */
const baseKeywordReducers = {
    addNewFilter: <T extends FilterState>(state: T, action: PayloadAction<string>) => {
        if (!state.filters.keywords.includes(action.payload)) {
            state.filters.keywords.push(action.payload);
        }
    },
    removeFilter: <T extends FilterState>(state: T, action: PayloadAction<string>) => {
        const index = state.filters.keywords.indexOf(action.payload);
        if (index > -1) {
            state.filters.keywords.splice(index, 1);
        }
    },
};

/**
 * Shared filter reducers that can be spread into any slice's reducers.
 * Usage:
 * ```
 * const mySlice = createSlice({
 *     name: 'mySlice',
 *     initialState: createFilterInitialState(),
 *     reducers: {
 *         ...filterReducers,
 *         // additional custom reducers
 *     }
 * });
 * ```
 */
export const filterReducers = {
    addNewFilter: baseKeywordReducers.addNewFilter<FilterState>,
    removeFilter: baseKeywordReducers.removeFilter<FilterState>,
    clearFilters: (state: FilterState) => {
        state.filters.keywords = [];
    },
};

/**
 * Creates dropdown filter reducers with a custom clearFilters that returns to provided initial state.
 * Use this when clearFilters needs to reset the entire state including dropdowns.
 */
export const createDropdownFilterReducers = <T extends DropdownFilterState>(initialState: T) => ({
    addNewFilter: baseKeywordReducers.addNewFilter<T>,
    removeFilter: baseKeywordReducers.removeFilter<T>,
    clearFilters: () => initialState,
    checkClaimTypeFilter: (state: T, action: PayloadAction<string>) => {
        const keys = state.filters.dropdowns.claimDataTypeName.keys;
        const index = keys.indexOf(action.payload);
        if (index === -1) {
            keys.push(action.payload);
        } else {
            keys.splice(index, 1);
        }
    },
});

/**
 * Creates status dropdown filter reducers with a custom clearFilters that returns to provided initial state.
 * Use this for slices that have status dropdown filters.
 */
export const createStatusDropdownFilterReducers = <T extends StatusDropdownFilterState>(initialState: T) => ({
    addNewFilter: baseKeywordReducers.addNewFilter<T>,
    removeFilter: baseKeywordReducers.removeFilter<T>,
    clearFilters: () => initialState,
    checkStatusFilter: (state: T, action: PayloadAction<string>) => {
        const keys = state.filters.dropdowns.status.keys;
        const index = keys.indexOf(action.payload);
        if (index === -1) {
            keys.push(action.payload);
        } else {
            keys.splice(index, 1);
        }
    },
});
